import { findBand, BAND_COLORS } from "../bands";

// ASCVD — 10-year atherosclerotic cardiovascular disease risk, ACC/AHA Pooled
// Cohort Equations (Goff et al. 2013). Coefficients transcribed from the
// guideline's published appendix and verified against its four worked examples.
// ⚠️ Keep a clinician in the loop before relying on these clinically.
const ascvdBands = [
    { label: "Low risk", color: BAND_COLORS.remission, lt: 5 },
    { label: "Borderline risk", color: BAND_COLORS.low, lt: 7.5 },
    { label: "Intermediate risk", color: BAND_COLORS.moderate, lt: 20 },
    { label: "High risk", color: BAND_COLORS.high },
];

// One coefficient set per sex × race group. Terms not in a group's published
// model are simply absent. meanSum and s10 (baseline survival) come from the
// same table; risk = 1 − s10^exp(sum − meanSum).
const GROUPS = {
    whiteFemale: {
        lnAge: -29.799, lnAgeSq: 4.884, lnTC: 13.540, lnAgeLnTC: -3.114,
        lnHDL: -13.578, lnAgeLnHDL: 3.149,
        lnSBPTreated: 2.019, lnSBPUntreated: 1.957,
        smoker: 7.574, lnAgeSmoker: -1.665, diabetes: 0.661,
        meanSum: -29.18, s10: 0.9665,
    },
    blackFemale: {
        lnAge: 17.114, lnTC: 0.940,
        lnHDL: -18.920, lnAgeLnHDL: 4.475,
        lnSBPTreated: 29.291, lnAgeLnSBPTreated: -6.432,
        lnSBPUntreated: 27.820, lnAgeLnSBPUntreated: -6.087,
        smoker: 0.691, diabetes: 0.874,
        meanSum: 86.61, s10: 0.9533,
    },
    whiteMale: {
        lnAge: 12.344, lnTC: 11.853, lnAgeLnTC: -2.664,
        lnHDL: -7.990, lnAgeLnHDL: 1.769,
        lnSBPTreated: 1.797, lnSBPUntreated: 1.764,
        smoker: 7.837, lnAgeSmoker: -1.795, diabetes: 0.658,
        meanSum: 61.18, s10: 0.9144,
    },
    blackMale: {
        lnAge: 2.469, lnTC: 0.302, lnHDL: -0.307,
        lnSBPTreated: 1.916, lnSBPUntreated: 1.809,
        smoker: 0.549, diabetes: 0.645,
        meanSum: 19.54, s10: 0.8954,
    },
};

export const ascvd = {
    id: "ascvd",
    name: "ASCVD Risk",
    subtitle: "10-year cardiovascular risk (Pooled Cohort Equations)",
    group: "Risk assessment",
    status: "ready",
    layout: "stacked",
    fields: [
        // Sex and race are patient attributes, not score variants, so they are
        // fields (one saved ASCVD row per visit) rather than options.
        {
            key: "sex",
            label: "Sex",
            type: "select",
            choices: [
                { label: "Male", value: 1 },
                { label: "Female", value: 0 },
            ],
        },
        {
            key: "race",
            label: "Race",
            type: "select",
            default: 0,
            choices: [
                { label: "White / other", value: 0 },
                { label: "African American", value: 1 },
            ],
            hint: "The equations have only these two branches. Use White/other for South Asian patients — the score may underestimate their risk (risk enhancer per the 2018 guideline).",
        },
        { key: "age", label: "Age", unit: "years", type: "number", min: 40, max: 79 },
        { key: "totalChol", label: "Total cholesterol", unit: "mg/dL", type: "number", min: 130, max: 320 },
        { key: "hdl", label: "HDL cholesterol", unit: "mg/dL", type: "number", min: 20, max: 100 },
        { key: "sbp", label: "Systolic blood pressure", unit: "mm Hg", type: "number", min: 90, max: 200 },
        { key: "bpTreated", label: "On blood-pressure medication", type: "toggle", default: 0 },
        { key: "diabetes", label: "Diabetes", type: "toggle", default: 0 },
        { key: "smoker", label: "Current smoker", type: "toggle", default: 0 },
    ],
    compute: (v) => {
        const g =
            v.sex === 1
                ? v.race === 1 ? GROUPS.blackMale : GROUPS.whiteMale
                : v.race === 1 ? GROUPS.blackFemale : GROUPS.whiteFemale;

        const lnAge = Math.log(v.age);
        const lnTC = Math.log(v.totalChol);
        const lnHDL = Math.log(v.hdl);
        const lnSBP = Math.log(v.sbp);
        const treated = v.bpTreated === 1;

        let sum = 0;
        sum += g.lnAge * lnAge;
        if (g.lnAgeSq) sum += g.lnAgeSq * lnAge * lnAge;
        sum += g.lnTC * lnTC;
        if (g.lnAgeLnTC) sum += g.lnAgeLnTC * lnAge * lnTC;
        sum += g.lnHDL * lnHDL;
        if (g.lnAgeLnHDL) sum += g.lnAgeLnHDL * lnAge * lnHDL;
        sum += (treated ? g.lnSBPTreated : g.lnSBPUntreated) * lnSBP;
        if (treated && g.lnAgeLnSBPTreated) sum += g.lnAgeLnSBPTreated * lnAge * lnSBP;
        if (!treated && g.lnAgeLnSBPUntreated) sum += g.lnAgeLnSBPUntreated * lnAge * lnSBP;
        if (v.smoker === 1) {
            sum += g.smoker;
            if (g.lnAgeSmoker) sum += g.lnAgeSmoker * lnAge;
        }
        if (v.diabetes === 1) sum += g.diabetes;

        const risk = 1 - Math.pow(g.s10, Math.exp(sum - g.meanSum));
        return risk * 100; // percent
    },
    interpret: (score) => findBand(score, ascvdBands),
    bands: () => ascvdBands,
    about:
        "Estimates the 10-year risk of a first hard atherosclerotic cardiovascular event (nonfatal myocardial infarction, coronary heart disease death, or stroke) for adults aged 40–79 without established ASCVD, using the ACC/AHA Pooled Cohort Equations. Risk bands (low <5%, borderline 5–7.4%, intermediate 7.5–19.9%, high ≥20%) guide statin therapy decisions per the 2018 cholesterol guideline. The equations were derived from US cohorts; they tend to underestimate risk in South Asian patients, which the guideline treats as a risk-enhancing factor.",
    references: [
        "Goff DC Jr, et al. 2013 ACC/AHA guideline on the assessment of cardiovascular risk. Circulation. 2014.",
        "Grundy SM, et al. 2018 AHA/ACC guideline on the management of blood cholesterol. Circulation. 2019.",
    ],
};
